'use client'

import {useCallback, useEffect, useState} from 'react'
import {message} from '@/components/Toast'
import {apiDelete, apiGet, apiPost} from '@/lib/api'
import {emptySettings} from '@/lib/constants'
import type {Account, KeywordRule, ScheduledTask, SettingsState} from '@/types'

type BotData = {
    rules: KeywordRule[]
    tasks: ScheduledTask[]
    settings: SettingsState
    saveRule: (rule: Partial<KeywordRule>) => Promise<void>
    deleteRule: (id: number) => Promise<void>
    saveTask: (task: Partial<ScheduledTask>) => Promise<void>
    deleteTask: (id: number) => Promise<void>
    /** Re-fetch the scheduled-task list on demand (no automatic polling). */
    refreshTasks: () => Promise<void>
    saveSettings: (patch: Partial<SettingsState>) => Promise<void>
}

function settingSavedMessage(patch: Partial<SettingsState>, current: SettingsState): string {
    if (patch.system) {
        if (
            typeof patch.system.messageNotifications === 'boolean' &&
            patch.system.messageNotifications !== current.system.messageNotifications
        ) {
            return patch.system.messageNotifications ? '已开启消息系统通知' : '已关闭消息系统通知'
        }
        if (
            patch.system.aiReplyHistoryLimit !== current.system.aiReplyHistoryLimit ||
            patch.system.aiReplyPrompt !== current.system.aiReplyPrompt
        ) {
            return 'AI 生成回复设置已保存'
        }
        return '系统设置已保存'
    }

    if (patch.tts) {
        if (patch.tts.enabled !== current.tts.enabled) {
            return patch.tts.enabled ? '已开启文字转语音' : '已关闭文字转语音'
        }
        if (patch.tts.selectedVoiceId !== current.tts.selectedVoiceId) {
            return '文字转语音声音已切换'
        }
        return '文字转语音设置已保存'
    }

    if (patch.voiceChanger) {
        if (patch.voiceChanger.enabled !== current.voiceChanger.enabled) {
            return patch.voiceChanger.enabled ? '已开启变声器' : '已关闭变声器'
        }
        if (patch.voiceChanger.selectedModelId !== current.voiceChanger.selectedModelId) {
            return '变声器声音已切换'
        }
        return '变声器设置已保存'
    }

    if (patch.security) {
        if (patch.security.autoAcceptFriend !== current.security.autoAcceptFriend) {
            return patch.security.autoAcceptFriend ? '已开启自动通过好友申请' : '已关闭自动通过好友申请'
        }
        return '自动化设置已保存'
    }

    if (patch.aiReply) {
        if (patch.aiReply.aiEnabled !== current.aiReply.aiEnabled) {
            return patch.aiReply.aiEnabled ? '已开启 AI 回复' : '已关闭 AI 回复'
        }
        if (patch.aiReply.keywordEnabled !== current.aiReply.keywordEnabled) {
            return patch.aiReply.keywordEnabled ? '已开启关键词回复' : '已关闭关键词回复'
        }
        if (patch.aiReply.prompt !== current.aiReply.prompt) {
            return 'AI 回复 Prompt 已保存'
        }
        return '自动回复设置已保存'
    }

    if (patch.groupWelcome) return '入群欢迎语已保存'
    if (patch.groupAutoReply) return '群自动回复已保存'
    if (patch.aiConfig) return 'AI 配置已保存'
    if (patch.proxy) return '代理设置已保存'
    return '设置已保存'
}

function mergeSettings(settings?: Partial<SettingsState>): SettingsState {
    return {
        ...emptySettings,
        ...settings,
        aiConfig: {...emptySettings.aiConfig, ...(settings?.aiConfig || {})},
        aiReply: {...emptySettings.aiReply, ...(settings?.aiReply || {})},
        security: {...emptySettings.security, ...(settings?.security || {})},
        system: {...emptySettings.system, ...(settings?.system || {})},
        voiceChanger: {...emptySettings.voiceChanger, ...(settings?.voiceChanger || {})},
        tts: {...emptySettings.tts, ...(settings?.tts || {})},
        proxy: {...emptySettings.proxy, ...(settings?.proxy || {})},
        groupWelcome: {...emptySettings.groupWelcome, ...(settings?.groupWelcome || {})},
        groupAutoReply: {...emptySettings.groupAutoReply, ...(settings?.groupAutoReply || {})},
    }
}

export function useBotData(account: Account | null): BotData {
    const [rules, setRules] = useState<KeywordRule[]>([])
    const [tasks, setTasks] = useState<ScheduledTask[]>([])
    const [settings, setSettings] = useState<SettingsState>(emptySettings)

    useEffect(() => {
        if (!account) {
            setRules([])
            setTasks([])
            return
        }
        const qs = 'wxid=' + encodeURIComponent(account.wxid)
        void apiGet<{ success: boolean; rules: KeywordRule[] }>(`/keywords?${qs}`).then((res) =>
            setRules(res.rules || []),
        )
        void apiGet<{ success: boolean; tasks: ScheduledTask[] }>(`/tasks?${qs}`).then((res) =>
            setTasks(res.tasks || []),
        )
    }, [account])

    // Settings are per-system-user (JWT), not per-wxid — always fetch
    useEffect(() => {
        void apiGet<{ success: boolean; settings: SettingsState }>('/settings').then((res) =>
            setSettings(mergeSettings(res.settings)),
        ).catch(() => setSettings(emptySettings))
    }, [])

    // Task list is no longer polled. The server-side scheduler drives delivery
    // (every 30 s per main.ts), and the UI re-fetches only when:
    //   - an account signs in (initial load above)
    //   - the user lands on /scheduled (ScheduledView calls `refreshTasks`
    //     on mount)
    //   - the user clicks the explicit "刷新" button in ScheduledView
    //   - a mutating call (saveTask / deleteTask) already returns the fresh list
    const refreshTasks = useCallback(async () => {
        if (!account) return
        const qs = 'wxid=' + encodeURIComponent(account.wxid)
        const response = await apiGet<{ success: boolean; tasks: ScheduledTask[] }>(
            `/tasks?${qs}`,
        )
        if (response.tasks) setTasks(response.tasks)
    }, [account])

    const saveRule = useCallback(
        async (rule: Partial<KeywordRule>) => {
            if (!account) return
            const previous = rule.id ? rules.find((item) => item.id === rule.id) : null
            const response = await apiPost<{ success: boolean; rules: KeywordRule[] }>(
                '/keywords',
                {...rule, wxid: account.wxid},
            )
            setRules(response.rules || [])
            if (previous && typeof rule.enabled === 'boolean' && previous.enabled !== rule.enabled) {
                message.success(rule.enabled ? '规则已开启' : '规则已关闭')
            } else {
                message.success(rule.id ? '规则已保存' : '规则已添加')
            }
        },
        [account, rules],
    )

    const deleteRule = useCallback(
        async (id: number) => {
            if (!account) return
            const response = await apiDelete<{ success: boolean; rules: KeywordRule[] }>(
                `/keywords?wxid=${encodeURIComponent(account.wxid)}&id=${id}`,
            )
            setRules(response.rules || [])
            message.success('规则已删除')
        },
        [account],
    )

    const saveTask = useCallback(
        async (task: Partial<ScheduledTask>) => {
            if (!account) return
            const response = await apiPost<{ success: boolean; tasks: ScheduledTask[] }>(
                '/tasks',
                {...task, wxid: account.wxid},
            )
            setTasks(response.tasks || [])
            message.success(task.id ? '任务已保存' : '任务已创建')
        },
        [account],
    )

    const deleteTask = useCallback(
        async (id: number) => {
            if (!account) return
            const response = await apiDelete<{ success: boolean; tasks: ScheduledTask[] }>(
                `/tasks?wxid=${encodeURIComponent(account.wxid)}&id=${id}`,
            )
            setTasks(response.tasks || [])
            message.success('任务已删除')
        },
        [account],
    )

    const saveSettings = useCallback(
        async (patch: Partial<SettingsState>) => {
            const next = mergeSettings({...settings, ...patch})
            setSettings(next)
            const response = await apiPost<{ success: boolean; settings: Partial<SettingsState> }>(
                '/settings',
                patch,
            )
            setSettings(mergeSettings(response.settings))
            message.success(settingSavedMessage(patch, settings))
        },
        [settings],
    )

    return {rules, tasks, settings, saveRule, deleteRule, saveTask, deleteTask, refreshTasks, saveSettings}
}
